"use client";

import { format } from "date-fns";
import { srLatn } from "date-fns/locale";
import { ChevronDown, UserRound } from "lucide-react";
import { useState } from "react";
import { type RouterOutput } from "~/server/api/root";

import { Avatar, AvatarFallback, AvatarImage } from "~/components/ui/avatar";
import { Badge } from "~/components/ui/badge";
import { isAnonymousReview } from "~/lib/session-review-identity";
import { cn } from "~/lib/utils";
import { CommentRatings } from "../chat/components/rating";

type Review = NonNullable<
  RouterOutput["therapistRouter"]["getTherapist"]
>["user"]["sessionReviews"][number];

type TherapistTestimonialsProps = {
  reviews: Review[];
  averageRating: number;
};

const INITIAL_COUNT = 3;

export function prioritizeWrittenReviews<
  T extends Pick<Review, "identityMode" | "review">,
>(reviews: T[]): T[] {
  const priority = (review: T) =>
    (review.review?.trim() ? 0 : 2) +
    (review.identityMode === "PUBLIC" ? 0 : 1);

  return reviews
    .map((review, index) => ({ review, index }))
    .sort(
      (first, second) =>
        priority(first.review) - priority(second.review) ||
        first.index - second.index,
    )
    .map(({ review }) => review);
}

function ReviewCard({
  review,
  flushTop = false,
}: {
  review: Review;
  flushTop?: boolean;
}) {
  const publicName = review.publicSender.name;
  const hasReviewText = !!review.review;
  const initials = (publicName ?? "")
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className={cn("py-5 last:pb-0", flushTop && "pt-0")}>
      <div className={cn("flex items-center gap-3", hasReviewText && "mb-3")}>
        {isAnonymousReview(review.identityMode) ? (
          <span className="border-brand/20 bg-brand/10 text-brand flex h-9 w-9 shrink-0 items-center justify-center rounded-full border">
            <UserRound className="h-4 w-4" />
          </span>
        ) : (
          <Avatar className="h-9 w-9 overflow-hidden">
            {review.publicSender.image && (
              <AvatarImage
                src={review.publicSender.image}
                alt=""
                className={cn(review.publicSender.blurImage && "blur-xs")}
              />
            )}
            <AvatarFallback className="text-xs">{initials}</AvatarFallback>
          </Avatar>
        )}
        <div className="flex-1">
          <p className="text-sm font-medium">{publicName}</p>
          <span className="text-muted-foreground text-xs capitalize">
            {format(review.createdAt, "LLLL yyyy", {
              locale: srLatn,
            })}
          </span>
        </div>
        <CommentRatings
          variant="yellow"
          disabled
          rating={review.rating}
          size={14}
          showText={false}
        />
      </div>

      {hasReviewText && (
        <p className="text-foreground text-sm leading-relaxed">
          {review.review}
        </p>
      )}
    </div>
  );
}

function RatingSummary({
  reviews,
  averageRating,
}: {
  reviews: Review[];
  averageRating: number;
}) {
  const distribution = [5, 4, 3, 2, 1].map((stars) => ({
    stars,
    count: reviews.filter((r) => r.rating === stars).length,
  }));
  const maxCount = Math.max(...distribution.map((d) => d.count));

  return (
    <div className="mb-6 flex items-center gap-6">
      <div className="shrink-0">
        <p className="text-5xl font-bold tabular-nums">
          {averageRating.toFixed(1)}
        </p>
        <div className="mt-1">
          <CommentRatings
            variant="yellow"
            disabled
            rating={averageRating}
            size={12}
            showText={false}
          />
        </div>
        <p className="text-muted-foreground mt-0.5 text-xs">
          {reviews.length} recenzija
        </p>
      </div>
      <div className="flex-1 space-y-1.5">
        {distribution.map(({ stars, count }) => (
          <div key={stars} className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground w-3 text-right text-xs">
              {stars}
            </span>
            <div className="bg-muted h-2 flex-1 overflow-hidden rounded-full">
              <div
                className="h-full rounded-full bg-yellow-400"
                style={{
                  width: maxCount > 0 ? `${(count / maxCount) * 100}%` : "0%",
                }}
              />
            </div>
            <span className="text-muted-foreground w-5 text-right text-xs">
              {count}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function TherapistTestimonials({
  reviews,
  averageRating,
}: TherapistTestimonialsProps) {
  const [showAll, setShowAll] = useState(false);

  if (!reviews.length) return null;

  const orderedReviews = prioritizeWrittenReviews(reviews);
  const remainingReviews = orderedReviews.slice(INITIAL_COUNT);
  const visibleReviews = showAll
    ? orderedReviews
    : orderedReviews.slice(0, INITIAL_COUNT);
  const showSummary = reviews.length >= 5;

  return (
    <section id="utisci-klijenata" className="mb-10 scroll-mt-28 border-t pt-8">
      <h2 className="mb-6 flex items-center gap-2 font-sans text-3xl font-medium">
        Utisci klijenata
        <Badge variant="secondary">{reviews.length}</Badge>
      </h2>

      {showSummary && (
        <RatingSummary reviews={reviews} averageRating={averageRating} />
      )}

      <div className="divide-y">
        {visibleReviews.map((review, index) => (
          <ReviewCard key={review.id} review={review} flushTop={index === 0} />
        ))}
      </div>

      {remainingReviews.length > 0 && (
        <button
          type="button"
          onClick={() => setShowAll((current) => !current)}
          className="text-muted-foreground hover:text-foreground mt-4 flex w-full items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors"
        >
          {showAll
            ? "Prikaži manje"
            : `Prikaži još ${remainingReviews.length} recenzija`}
          <ChevronDown
            className={cn(
              "h-4 w-4 transition-transform",
              showAll && "rotate-180",
            )}
          />
        </button>
      )}
    </section>
  );
}
